import React, { useEffect, useRef } from 'react';
import ChatBot from '../chatbot/ChatBot';
import magicIcon from '../common/Images/magic.svg';
import { httpPost, httpDelete } from '../utils';
import {
    destination,
    deviceOrServer,
    node,
    range,
    reason,
    ruleName,
    source,
    users
} from '../Controls/Filters/FiltersObjects';
import moment from 'moment';
import AcknowledgmentPopover from '../chatbot/ChatBotAuthorization';
import { useChatbotAuthStatus } from '../chatbot/useChatbotAuthStatus';

const LOGS_AMOUNT_DEFAULT_VALUE = -1;
const TITLE = 'Filter with AI';
const DESCRIPTION =
    "Understand your security status by analyzing authentication traffic in your monitored network. Ask questions, and Silverfort's AI engine will filter relevant authentication logs. Further refine by asking more questions or adjusting filters manually.";
const QUERY_PLACEHOLDER = 'What authentications are you looking for?';
const CLEAR_TEXT = 'Clear Filters';
const FILTERS_EXAMPLES = [
    'Show all NTLM logs from the last week that have a high risk level',
    'Present authentications from the last month',
    'Show me all the RDP authentications from yesterday'
];
const DISCLAIMER = 'Not all table columns are supported by Filter with AI.';
const DISCLAIMER_EXTRA_INFO =
    'Currently, Filter with AI does not support the fields: Source or destination (combined), Deny reasons, Executed policy, Service accounts smart policies, and Node. You can filter for these fields manually.';

const FiltersChatBot = ({ open, onHide, userId, filterState, onFiltersChange, clearFilters, logsAmount }) => {
    const authControl = useChatbotAuthStatus();
    const { isAuthorizedToUse } = authControl;

    const unsupportedFilters = new Set([deviceOrServer.id, node.id, ruleName.id, reason.id]);
    const [messagesMetricsMap, setMessagesMetricsMap] = React.useState(new Map());
    const objectsFilters = new Set([users.id, source.id, destination.id]);
    const [chatBotId, setChatBotId] = React.useState('');
    const [isAnalyzing, setIsAnalyzing] = React.useState(false);
    const [messages, setMessages] = React.useState([]);
    const [currentMessage, setCurrentMessage] = React.useState(undefined);
    const [currentMessageFilters, setCurrentMessageFilters] = React.useState(undefined);
    const timerRef = useRef(null);
    const analyzing = useRef(false);
    const [inClearFiltersProcess, setInClearFiltersProcess] = React.useState(false);
    let messageTimeout = 15000;

    // We cant use useEffect return function because logs render more than one
    window.onbeforeunload = () => {
        httpDelete('/chats', { chatId: chatBotId, messagesMetrics: Array.from(messagesMetricsMap.values()) });
    };

    useEffect(() => {
        const events = new EventSource('/chats');
        events.onmessage = (event) => {
            clearTimeout(timerRef.current);
            const parsedData = JSON.parse(event.data);
            if (parsedData.chatId != null) {
                setChatBotId(parsedData.chatId);
                messageTimeout = parsedData.messageTimeout;
            } else {
                if (analyzing.current) {
                    const message = parsedData.message;
                    if (!message.error) {
                        onFiltersChange(parsedData.filters, unsupportedFilters);
                        setCurrentMessage(parsedData.message);
                        const messageFilters = parsedData.filters
                            ? Object.fromEntries(
                                  Object.entries(parsedData.filters).filter(([key]) => !unsupportedFilters.has(key))
                              )
                            : null;
                        setCurrentMessageFilters(messageFilters);
                    } else {
                        setMessages((oldMessages) => [...oldMessages, parsedData.message]);
                        addAnswerMetrics(parsedData.message);
                        analyzing.current = false;
                        setIsAnalyzing(false);
                    }
                }
            }
        };

        events.onerror = (e) => {
            console.error(e);
        };
    }, []);

    useEffect(() => {
        if (logsAmount !== LOGS_AMOUNT_DEFAULT_VALUE) {
            if (inClearFiltersProcess) {
                clearAllFiltersDone();
            } else if (currentMessage) {
                currentMessage.content = `${logsAmount} authentications found`;
                setCurrentMessage(currentMessage);
                setMessages((oldMessages) => [...oldMessages, currentMessage]);
                analyzing.current = false;
                setIsAnalyzing(false);
                setCurrentMessage(undefined);
                addAnswerMetrics(currentMessage, currentMessageFilters);
                setCurrentMessageFilters(undefined);
            }
        }
    }, [logsAmount]);

    async function onMessageSubmit(inputValue) {
        if (!isAuthorizedToUse) {
            return;
        }

        setMessages((oldMessages) => [...oldMessages, { content: inputValue, userId: userId }]);
        analyzing.current = true;
        setIsAnalyzing(true);
        clearTimeout(timerRef.current);
        const res = await httpPost('/chats', {
            chatId: chatBotId,
            massage: inputValue,
            currentDate: moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
            filters: getSupportedFiltersState(filterState)
        });
        addMessagesMetrics(res.data.id, inputValue);
        timerRef.current = setTimeout(() => {
            if (analyzing.current) {
                setMessages((oldMessages) => [
                    ...oldMessages,
                    { connectionTimeout: 'Request failed due to low connectivity' }
                ]);
                analyzing.current = false;
                setIsAnalyzing(false);
            }
        }, messageTimeout);
    }

    const getSupportedFiltersState = (filterState) => {
        const newFilterSate = {};
        Object.keys(filterState)
            .filter(
                (key) => !unsupportedFilters.has(key) && filterState[key].value && filterState[key].value.length !== 0
            )
            .forEach((key) => {
                if (key === range.id) {
                    newFilterSate[key] = {
                        startTime: getDateStringFromEpoch(filterState[key].value[1]),
                        endTime: getDateStringFromEpoch(filterState[key].value[2])
                    };
                } else if (objectsFilters.has(key)) {
                    const values = [];
                    for (let i = 0; i < filterState[key].value.length; i++) {
                        values[i] = {
                            value: filterState[key].value[i].label,
                            excluded: filterState[key].value[i].excluded
                        };
                    }
                    newFilterSate[key] = {
                        value: values
                    };
                } else {
                    newFilterSate[key] = {
                        value: filterState[key].value,
                        excluded: filterState[key].isExcluded
                    };
                }
            });
        return newFilterSate;
    };

    const addMessagesMetrics = (messageId, massageData) => {
        const messageMetric = {
            chat_id: chatBotId,
            message_id: messageId,
            prompt: massageData
        };
        setMessagesMetricsMap(messagesMetricsMap.set(messageId, messageMetric));
    };

    const addAnswerMetrics = (message, filters = null) => {
        const messageMetric = {
            answer: message.content !== undefined ? message.content : message.error,
            timestamp: message.timestamp,
            generated_query: filters,
            ...messagesMetricsMap.get(message.id)
        };
        setMessagesMetricsMap(messagesMetricsMap.set(message.id, messageMetric));
    };

    const addFeedbackMetrics = (id, reaction) => {
        const newMessages = messages.map((m) => {
            if (m.id === id) {
                return { ...m, reaction: reaction };
            }
            return m;
        });

        setMessages(newMessages);
        const messageMetric = {
            feedback: reaction,
            ...messagesMetricsMap.get(id)
        };
        setMessagesMetricsMap(messagesMetricsMap.set(id, messageMetric));
    };

    const clearAllFilters = () => {
        setInClearFiltersProcess(true);
        clearFilters();

        analyzing.current = false;
    };
    const clearAllFiltersDone = () => {
        setInClearFiltersProcess(false);
        setIsAnalyzing(false);
        setMessages([]);
    };
    const getDateStringFromEpoch = (epoch) => {
        return moment(epoch * 1000).format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    };

    const disabled = isAnalyzing || !isAuthorizedToUse;

    return (
        <div className={open ? 'block' : 'hidden'}>
            <ChatBot
                open={true}
                title={TITLE}
                description={DESCRIPTION}
                queryPlaceholder={QUERY_PLACEHOLDER}
                clearText={CLEAR_TEXT}
                icon={magicIcon}
                disclaimer={DISCLAIMER}
                disclaimerExtraInfo={DISCLAIMER_EXTRA_INFO}
                onHide={onHide}
                onclose={onclose}
                examples={FILTERS_EXAMPLES}
                messages={messages}
                isAnalyzing={isAnalyzing}
                onClear={clearAllFilters}
                onMessageReaction={addFeedbackMetrics}
                inClearFiltersProcess={inClearFiltersProcess}
                onMessageSubmit={onMessageSubmit}
                AcknowledgmentPopover={<AcknowledgmentPopover onHide={onHide} authControl={authControl} />}
                disabled={disabled}
            />
        </div>
    );
};

export default FiltersChatBot;
